import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { CapacitorHttp, Capacitor } from "@capacitor/core";
import { contactIdFromNotificationData } from "../lib/notificationRoute.mjs";

let listenersInstalled = false;

export function contactIdFromNotification(event) {
  const data = event?.notification?.data || event?.notification?.extra?.data || {};
  return contactIdFromNotificationData(data);
}

/**
 * Native push (FCM) registration + inbound-notification handling.
 *
 * Requires a Firebase project + `google-services.json` (Android) +
 * `GoogleService-Info.plist` (iOS) — see /app/frontend/NATIVE_SETUP.md.
 * Without those credentials `getToken()` throws at runtime.
 */
const NotificationService = {
  /**
   * Registers the current device's FCM token with our backend so the
   * server can push alerts to native iOS/Android apps.
   */
  async register({ sessionToken, backendUrl }) {
    if (!Capacitor.isNativePlatform()) return { skipped: "web" };
    if (!sessionToken || !backendUrl) return { skipped: "missing_context" };

    const perm = await FirebaseMessaging.requestPermissions();
    if (perm?.receive !== "granted") return { skipped: "permission_denied" };

    let token;
    try {
      const r = await FirebaseMessaging.getToken();
      token = r?.token;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("FCM getToken failed (check google-services.json / GoogleService-Info.plist):", e?.message || e);
      return { skipped: "firebase_config_missing" };
    }
    if (!token) return { skipped: "no_token" };

    const platform = Capacitor.getPlatform(); // "ios" | "android" | "web"
    try {
      await CapacitorHttp.post({
        url: `${backendUrl}/api/push/fcm-token`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`,
        },
        data: { token, platform },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("register FCM token failed:", e?.message || e);
      return { skipped: "backend_reject" };
    }

    // Foreground notification listener (background-delivery is handled
    // natively by the OS from the manifest / plist).
    try {
      if (listenersInstalled) return { ok: true, platform, token, listeners: "existing" };
      await FirebaseMessaging.addListener("notificationReceived", (event) => {
        // eslint-disable-next-line no-console
        console.log("FCM notification received (foreground):", event?.notification);
      });
      await FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
        const contactUserId = contactIdFromNotification(event);
        window.dispatchEvent(new CustomEvent("proximity:open-alert", { detail: { contactUserId } }));
      });
      listenersInstalled = true;
    } catch {
      /* listeners are additive — non-fatal */
    }
    return { ok: true, platform, token };
  },
};

export default NotificationService;
